import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle,
  Gift,
  Mail,
  Sparkles,
  Users,
} from 'lucide-react-native';
import {
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  type TextStyle,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import { FadeIn } from '../src/components/animations';
import { usePulseAnimation } from '../src/components/animations/useAnimations';
import { LanguageSwitcher } from '../src/components/LanguageSwitcher';
import { GridBackdrop } from '../src/components/landing/landingStyleKit';
import { WaitlistForm } from '../src/components/landing/WaitlistForm';
import { Container } from '../src/components/primitives/Container';
import { Text } from '../src/components/primitives/Text';
import { useTranslation } from '../src/contexts/TranslationContext';
import { useHoverState } from '../src/hooks/useHoverState';
import { trackEvent } from '../src/lib/analytics';
import { useMixpanel } from '../src/lib/mixpanel';
import { colors } from '../src/theme/colors';
import { withAlpha } from '../src/theme/colorUtils';
import { easingCss } from '../src/theme/motion';
import { spacing } from '../src/theme/spacing';
import { tokens } from '../src/theme/tokens';
import { darkPrecisionFontFamilies } from '../src/theme/typography';

/**
 * The two-column split is desktop-only AND web-only: native (iOS/Android)
 * always renders the single stacked column, regardless of width.
 */
export const shouldUseSplitLayout = (width: number, os: typeof Platform.OS): boolean =>
  width >= tokens.breakpoints.desktop && os === 'web';

const webTransition = (properties: string): ViewStyle =>
  (Platform.select({
    web: {
      transitionProperty: properties,
      transitionDuration: '260ms',
      transitionTimingFunction: easingCss.out,
    },
    default: {},
  }) ?? {}) as ViewStyle;

const cardTransition = webTransition('transform, border-color, background-color, box-shadow');
const accentTransition = webTransition('transform, opacity');
const arrowTransition = webTransition('transform, opacity');

// Teal→cyan→blue gradient clipped to the headline accent word (web); native
// falls back to a flat neon fill since RN has no text-gradient primitive.
const gradientText = (Platform.select({
  web: {
    color: 'transparent',
    backgroundImage: colors.gradients.brandText,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  default: { color: colors.primary.neon },
}) ?? {}) as TextStyle;

export default function WaitlistPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const mp = useMixpanel();
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;
  const isTablet = width >= tokens.breakpoints.tablet;
  const isDesktopWeb = shouldUseSplitLayout(width, Platform.OS);

  const handleBackToHome = () => {
    trackEvent(mp, 'landing.waitlist_back_to_home', {});
    router.push('/');
  };

  const hero = (
    <View
      style={[
        styles.heroSection,
        isTablet && styles.heroSectionTablet,
        isDesktopWeb && styles.heroSectionDesktop,
      ]}
    >
      <FadeIn delay={100} translateY={16} scaleFrom={0.96}>
        <View style={styles.badge}>
          <Sparkles size={14} color={colors.primary[400]} />
          <Text style={styles.badgeText}>{t('pages.waitlist.earlyAccess')}</Text>
        </View>
      </FadeIn>

      <FadeIn delay={170} translateY={22}>
        <Text
          style={[styles.title, isDesktop && styles.titleDesktop, isDesktopWeb && styles.titleLeft]}
        >
          {t('pages.waitlist.joinThe')}
          <Text
            style={[styles.titleHighlight, isDesktop && styles.titleHighlightDesktop, gradientText]}
          >
            {t('pages.waitlist.future')}
          </Text>
          {t('pages.waitlist.ofSales')}
        </Text>
      </FadeIn>

      <FadeIn delay={240} translateY={18}>
        <Text
          style={[
            styles.subtitle,
            isTablet && styles.subtitleTablet,
            isDesktopWeb && styles.subtitleLeft,
          ]}
        >
          {t('pages.waitlist.subtitle')}
        </Text>
      </FadeIn>
    </View>
  );

  const form = (
    <FadeIn delay={isDesktopWeb ? 220 : 360} translateY={24} scaleFrom={0.97}>
      <View style={[styles.formCard, isTablet && styles.formCardTablet]}>
        <FormGlow />
        <LinearGradient
          colors={[colors.background.elevated, colors.background.secondary]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.formInner}
        >
          <LinearGradient
            colors={['transparent', colors.primary[400], 'transparent']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.cardTopAccent}
          />
          <Text style={styles.formTitle}>{t('pages.waitlist.reserveSpot')}</Text>
          <Text style={styles.formSubtitle}>{t('pages.waitlist.reserveSubtitle')}</Text>
          <WaitlistForm
            onSuccess={() => {
              /* noop */
            }}
          />
        </LinearGradient>
      </View>
    </FadeIn>
  );

  const benefitData = [
    {
      icon: Sparkles,
      title: t('pages.waitlist.benefits.earlyAccess.title'),
      description: t('pages.waitlist.benefits.earlyAccess.description'),
    },
    {
      icon: Gift,
      title: t('pages.waitlist.benefits.launchPricing.title'),
      description: t('pages.waitlist.benefits.launchPricing.description'),
    },
    {
      icon: Mail,
      title: t('pages.waitlist.benefits.productUpdates.title'),
      description: t('pages.waitlist.benefits.productUpdates.description'),
    },
    {
      icon: Users,
      title: t('pages.waitlist.benefits.shapeProduct.title'),
      description: t('pages.waitlist.benefits.shapeProduct.description'),
    },
  ];

  const benefits = (
    <View style={[styles.benefits, isTablet && styles.benefitsTablet]}>
      <FadeIn delay={isDesktopWeb ? 320 : 420} translateY={14}>
        <Text style={[styles.benefitsTitle, isDesktopWeb && styles.benefitsTitleLeft]}>
          {t('pages.waitlist.whatYouGet')}
        </Text>
      </FadeIn>

      <View
        testID="waitlist-benefits-list"
        style={[styles.benefitsList, isTablet && !isDesktopWeb && styles.benefitsListTablet]}
      >
        {benefitData.map((benefit, index) => (
          <FadeIn
            key={benefit.title}
            delay={(isDesktopWeb ? 380 : 480) + index * 90}
            translateY={16}
            scaleFrom={0.96}
            style={[styles.benefitSlot, isDesktopWeb && styles.benefitSlotFull]}
          >
            <BenefitCard
              icon={benefit.icon}
              title={benefit.title}
              description={benefit.description}
            />
          </FadeIn>
        ))}
      </View>
    </View>
  );

  const trust = (
    <FadeIn delay={680} translateY={14}>
      <View style={[styles.trustBadges, isDesktopWeb && styles.trustBadgesLeft]}>
        <View style={styles.trustBadge}>
          <CheckCircle size={16} color={colors.primary[500]} />
          <Text style={styles.trustText}>{t('pages.waitlist.trustBadges.noCreditCard')}</Text>
        </View>
        <View style={styles.trustBadge}>
          <CheckCircle size={16} color={colors.primary[500]} />
          <Text style={styles.trustText}>{t('pages.waitlist.trustBadges.cancelAnytime')}</Text>
        </View>
        <View style={styles.trustBadge}>
          <CheckCircle size={16} color={colors.primary[500]} />
          <Text style={styles.trustText}>{t('pages.waitlist.trustBadges.prioritySupport')}</Text>
        </View>
      </View>
    </FadeIn>
  );

  return (
    <View style={styles.container}>
      <GridBackdrop />
      <ScrollView style={styles.scroll} contentContainerStyle={styles.contentContainer}>
        <View style={styles.header}>
          <FadeIn delay={0} translateY={10}>
            <Pressable style={styles.backButton} onPress={handleBackToHome}>
              <ArrowLeft size={20} color={colors.text.secondary} />
              <Text style={styles.backText}>{t('pages.waitlist.backToHome')}</Text>
            </Pressable>
          </FadeIn>

          {Platform.OS === 'web' && (
            <FadeIn delay={50} translateY={10}>
              <LanguageSwitcher condensed />
            </FadeIn>
          )}
        </View>

        <Container style={styles.mainContainer}>
          {isDesktopWeb ? (
            <View testID="waitlist-content" style={[styles.content, styles.contentDesktopRow]}>
              <View testID="waitlist-left-rail" style={styles.leftRailDesktop}>
                {hero}
                {benefits}
              </View>
              <View testID="waitlist-right-rail" style={styles.rightRailDesktop}>
                {form}
                {trust}
              </View>
            </View>
          ) : (
            <View
              testID="waitlist-content"
              style={[styles.content, isDesktop && styles.contentDesktop]}
            >
              {hero}
              {form}
              {benefits}
              {trust}
            </View>
          )}
        </Container>
      </ScrollView>
    </View>
  );
}

function FormGlow() {
  const { opacity } = usePulseAnimation({ minOpacity: 0.12, maxOpacity: 0.24, duration: 4200 });
  return <Animated.View style={[styles.formGlow, { opacity }]} />;
}

interface BenefitCardProps {
  icon: typeof Sparkles;
  title: string;
  description: string;
}

const BenefitCard = ({ icon: Icon, title, description }: BenefitCardProps) => {
  const { isHovered, hoverProps } = useHoverState();

  return (
    <Pressable
      {...hoverProps}
      style={[styles.benefitItem, cardTransition, isHovered && styles.benefitItemHover]}
    >
      <View
        style={[styles.benefitAccent, accentTransition, isHovered && styles.benefitAccentHover]}
      />
      <LinearGradient
        colors={
          isHovered
            ? [colors.primary.subtle, colors.primary.muted]
            : [colors.surface.iconWell, colors.surface.row]
        }
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.benefitIconContainer}
      >
        <Icon
          size={20}
          color={isHovered ? colors.primary[300] : colors.primary[500]}
          strokeWidth={1.5}
        />
      </LinearGradient>
      <View style={styles.benefitContent}>
        <Text style={styles.benefitTitle}>{title}</Text>
        <Text style={styles.benefitDescription}>{description}</Text>
      </View>
      <View style={[styles.benefitArrow, arrowTransition, isHovered && styles.benefitArrowHover]}>
        <ArrowUpRight size={16} color={colors.primary[400]} />
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  scroll: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    flexGrow: 1,
    paddingBottom: Platform.select({ web: 80, default: spacing.xxl }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: Platform.select({ web: spacing.lg, default: spacing.xl }),
    paddingBottom: spacing.md,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    padding: spacing.sm,
    marginLeft: -spacing.sm,
  },
  backText: {
    color: colors.text.secondary,
    fontSize: 14,
    fontWeight: '500',
  },
  mainContainer: {
    flex: 1,
  },
  content: {
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },
  contentDesktop: {
    maxWidth: 700,
  },
  contentDesktopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: spacing.xxl,
    maxWidth: 1120,
  },
  leftRailDesktop: {
    flex: 1.1,
    minWidth: 0,
    alignItems: 'flex-start',
    maxWidth: 560,
  },
  rightRailDesktop: {
    flex: 1,
    maxWidth: 480,
    width: '100%',
    ...Platform.select({
      web: {
        position: 'sticky' as 'relative',
        top: spacing.lg,
      },
      default: {},
    }),
  },
  heroSection: {
    alignItems: 'center',
    marginBottom: spacing.xl,
    width: '100%',
  },
  heroSectionTablet: {
    marginBottom: spacing.xxl,
  },
  heroSectionDesktop: {
    alignItems: 'flex-start',
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary.muted,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
    borderRadius: tokens.radius.full,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    marginBottom: spacing.lg,
  },
  badgeText: {
    color: colors.primary[400],
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1,
  },
  title: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: Platform.select({ web: 40, default: 28 }),
    fontWeight: '800',
    textAlign: 'center',
    color: colors.text.primary,
    marginBottom: spacing.md,
    letterSpacing: Platform.select({ web: -1.5, default: -0.5 }),
    lineHeight: Platform.select({ web: 46, default: 36 }),
  },
  titleDesktop: {
    fontSize: 64,
    lineHeight: 66,
    letterSpacing: -2,
  },
  titleLeft: {
    textAlign: 'left',
  },
  titleHighlight: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontWeight: '800',
    fontSize: Platform.select({ web: 40, default: 28 }),
    lineHeight: Platform.select({ web: 46, default: 36 }),
    color: colors.primary[500],
  },
  titleHighlightDesktop: {
    fontSize: 64,
    lineHeight: 66,
  },
  subtitle: {
    fontSize: Platform.select({ web: 18, default: 15 }),
    textAlign: 'center',
    color: colors.text.secondary,
    lineHeight: Platform.select({ web: 29, default: 24 }),
    maxWidth: 500,
    marginBottom: spacing.lg,
  },
  subtitleTablet: {
    fontSize: 17,
    lineHeight: 28,
  },
  subtitleLeft: {
    textAlign: 'left',
  },
  formCard: {
    width: '100%',
    position: 'relative',
    marginBottom: spacing.xxl,
  },
  formCardTablet: {
    maxWidth: 480,
  },
  formGlow: {
    position: 'absolute',
    top: -2,
    left: -2,
    right: -2,
    bottom: -2,
    borderRadius: 18,
    backgroundColor: colors.primary[500],
    opacity: 0.15,
    ...Platform.select({
      web: {
        boxShadow: `0 0 40px ${withAlpha(colors.primary[500], 0.25)}, 0 0 80px ${withAlpha(colors.primary[500], 0.13)}`,
      },
      default: {
        shadowColor: colors.primary[500],
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 20,
        elevation: 10,
      },
    }),
  },
  formInner: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border.muted,
    padding: Platform.select({ web: spacing.xl, default: spacing.lg }),
    overflow: 'hidden',
  },
  cardTopAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    opacity: 0.7,
  },
  formTitle: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: Platform.select({ web: 28, default: 22 }),
    lineHeight: Platform.select({ web: 34, default: 28 }),
    fontWeight: '700',
    letterSpacing: -0.5,
    color: colors.text.primary,
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
  formSubtitle: {
    fontSize: 15,
    color: colors.text.secondary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  benefits: {
    width: '100%',
    marginBottom: spacing.xl,
  },
  benefitsTablet: {
    maxWidth: 520,
  },
  benefitsTitle: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: Platform.select({ web: 22, default: 18 }),
    lineHeight: Platform.select({ web: 28, default: 23 }),
    fontWeight: '700',
    color: colors.text.primary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  benefitsTitleLeft: {
    textAlign: 'left',
  },
  benefitsList: {
    gap: spacing.md,
  },
  benefitsListTablet: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  benefitSlot: {
    ...Platform.select({
      web: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '45%' as const,
        minWidth: 220,
      },
      default: {
        width: '100%',
      },
    }),
  } as const,
  benefitSlotFull: {
    flexBasis: '100%' as const,
    minWidth: 0,
    width: '100%',
  },
  benefitItem: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingLeft: spacing.lg,
    paddingRight: spacing.md,
    backgroundColor: colors.surface.row,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  benefitItemHover: {
    transform: [{ translateY: -4 }],
    borderColor: colors.primary.borderActive,
    backgroundColor: colors.surface.rowHover,
    ...Platform.select({
      web: {
        boxShadow: `0 16px 36px -16px ${withAlpha(colors.primary[500], 0.33)}`,
      },
      default: {},
    }),
  },
  benefitAccent: {
    position: 'absolute',
    left: 0,
    top: 14,
    bottom: 14,
    width: 3,
    borderTopRightRadius: 3,
    borderBottomRightRadius: 3,
    backgroundColor: colors.primary[500],
    opacity: 0,
    transform: [{ scaleY: 0.3 }],
  },
  benefitAccentHover: {
    opacity: 1,
    transform: [{ scaleY: 1 }],
  },
  benefitIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  benefitContent: {
    flex: 1,
  },
  benefitTitle: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: 17,
    lineHeight: 22,
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  benefitDescription: {
    fontSize: 14,
    color: colors.text.secondary,
    lineHeight: 20,
  },
  benefitArrow: {
    opacity: 0,
    transform: [{ translateX: -6 }],
  },
  benefitArrowHover: {
    opacity: 1,
    transform: [{ translateX: 0 }],
  },
  trustBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing.lg,
  },
  trustBadgesLeft: {
    justifyContent: 'flex-start',
  },
  trustBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  trustText: {
    fontSize: 13,
    color: colors.text.secondary,
  },
});
